import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { pathOr } from 'ramda'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import { PRIVATE_PATHS } from 'utils/paths'
import storageService from 'services/LocalStorageService'

const dismissKey = userId => `avatarPromptDismissed:${userId}`

const AvatarPromptBanner = () => {
  const user = useSelector(getCurrentUser)
  const navigate = useNavigate()
  const userId = user?.id
  const [dismissed, setDismissed] = useState(() => (userId ? storageService.get(dismissKey(userId)) === '1' : true))

  const isFromInfoExchange = pathOr('', ['source', 'value'], user) === 'info_exchange'
  const hasAvatar = isNotNilOrEmpty(user?.avatar) && !isFromInfoExchange

  if (!userId || hasAvatar || isFromInfoExchange || dismissed) return null

  const dismiss = e => {
    e.stopPropagation()
    storageService.set(dismissKey(userId), '1')
    setDismissed(true)
  }

  return createPortal(
    <Pill type='button' onClick={() => navigate(PRIVATE_PATHS.profileSettings)}>
      <Icon><PhotoCameraRoundedIcon style={{ fontSize: 18 }} /></Icon>
      <Text>Add a profile photo so colleagues recognise you</Text>
      <Go>Add photo <ArrowForwardRoundedIcon style={{ fontSize: 15 }} /></Go>
      <Close role='button' tabIndex={0} aria-label='Dismiss' onClick={dismiss}>
        <CloseRoundedIcon style={{ fontSize: 16 }} />
      </Close>
    </Pill>,
    document.body,
  )
}

export default AvatarPromptBanner

const rise = keyframes`from { opacity: 0; transform: translate(-50%, -16px) } to { opacity: 1; transform: translate(-50%, 0) }`

const Pill = styled.button`
  position: fixed;
  left: 50%;
  top: 16px;
  transform: translateX(-50%);
  z-index: 9000;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 16px;
  border: 1px solid #e4e6eb;
  background: #0c1229;
  color: #fff;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(12, 18, 41, 0.28);
  animation: ${rise} 0.28s cubic-bezier(0.22, 0.61, 0.36, 1) both;
  font: inherit;
  max-width: calc(100vw - 32px);

  &:hover { background: #141b38; }
`

const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #fbbf24;
`

const Text = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 560px) { display: none; }
`

const Go = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 700;
  color: #0c1229;
  background: #fff;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
`

const Close = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;

  &:hover { color: #fff; background: rgba(255, 255, 255, 0.12); }
`
